import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, ChevronLeft, MessageCircle, MessagesSquare, Minus, X } from "lucide-react";
import { Button, NoticeBox } from "../ui";
import { Context } from "../../context/Context";
import ChatView from "./ChatView";
import ConversationList from "./ConversationList";
import FaqView from "./FaqView";
import GuidedIntake from "./GuidedIntake";
import WelcomeView from "./WelcomeView";
import { useCopilotController } from "./useCopilotController";
import SettingsView from "./SettingsView";
import { COPILOT_OPEN_EVENT, readLinkFromUrl } from "./copilotLinks";
import "./CopilotWidget.css";

const OPEN_KEY = "lud-copilot-open";

const readOpen = () => {
  try {
    return sessionStorage.getItem(OPEN_KEY) === "1";
  } catch {
    return false;
  }
};
const writeOpen = (value) => {
  try {
    sessionStorage.setItem(OPEN_KEY, value ? "1" : "0");
  } catch {
    // UI preference only
  }
};

// Global LUD Copilot support widget: a floating button on every page and a
// chat panel. Mounted once (App); it renders into document.body so no page
// container can clip or stack over it. Conversation state lives in
// useCopilotController and survives minimizing and route changes.
export default function CopilotWidget() {
  const { lang, isAuthenticated, currentUserInfo } = useContext(Context);
  const mode = !isAuthenticated ? "guest" : currentUserInfo ? "user" : "pending";
  const userKey = currentUserInfo?._id || currentUserInfo?.id || "";

  const [open, setOpen] = useState(readOpen);
  const [hasOpened, setHasOpened] = useState(readOpen);
  const [visible, setVisible] = useState(() => document.visibilityState !== "hidden");
  const launcher = useRef(null);
  const panel = useRef(null);
  const title = useRef(null);

  const c = useCopilotController({ mode, userKey, lang, user: currentUserInfo, open, visible });
  const { t, g } = c;

  // Deep links: e-mail links (/?copilot=<id>[#access=<token>]) and in-app
  // notifications (the "lud-copilot-open" event). A link read at start-up waits
  // until the session is known; the address bar is cleaned at once so the
  // single-use token never stays in the URL or the history.
  const pendingLink = useRef(readLinkFromUrl());
  const openPanelRef = useRef(null);
  const openLink = c.openLink;
  useEffect(() => {
    if (pendingLink.current && c.mode !== "pending") {
      const link = pendingLink.current;
      pendingLink.current = null;
      openPanelRef.current?.();
      openLink(link);
    }
  }, [c.mode, openLink]);
  useEffect(() => {
    const onOpen = (event) => {
      const conversationId = event?.detail?.conversationId;
      if (!conversationId) return;
      openPanelRef.current?.();
      openLink({ conversationId });
    };
    window.addEventListener(COPILOT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(COPILOT_OPEN_EVENT, onOpen);
  }, [openLink]);

  useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const openPanel = useCallback(() => {
    setHasOpened(true);
    setOpen(true);
    writeOpen(true);
  }, []);

  openPanelRef.current = openPanel;

  const closePanel = useCallback(() => {
    const hadFocus = panel.current?.contains(document.activeElement);
    setOpen(false);
    writeOpen(false);
    if (hadFocus) requestAnimationFrame(() => launcher.current?.focus());
  }, []);

  // Small screens: the panel is as tall as the VISIBLE viewport, so the composer stays above the
  // on-screen keyboard (100dvh is the fallback where the visual viewport API is missing).
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!open || !viewport) return undefined;
    const apply = () => {
      const el = panel.current;
      if (!el) return;
      if (window.matchMedia?.("(max-width: 640px)").matches) el.style.setProperty("--lc-vvh", `${Math.round(viewport.height)}px`);
      else el.style.removeProperty("--lc-vvh");
    };
    apply();
    viewport.addEventListener("resize", apply);
    viewport.addEventListener("scroll", apply);
    return () => {
      viewport.removeEventListener("resize", apply);
      viewport.removeEventListener("scroll", apply);
    };
  }, [open]);

  // Move focus into the panel when it opens from the button.
  const openedByUser = useRef(false);
  useEffect(() => {
    if (open && openedByUser.current) title.current?.focus();
    openedByUser.current = false;
  }, [open]);

  const onPanelKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      closePanel();
    }
  };

  const toggle = () => {
    if (open) closePanel();
    else {
      openedByUser.current = true;
      openPanel();
    }
  };

  const userName = currentUserInfo?.name || currentUserInfo?.email || "";
  const showBack = c.view !== "home";
  const canSettings = c.mode === "user";
  const unreadLabel = c.unread > 0 ? `${t.open}. ${t.unread(c.unread)}` : t.open;
  const headingId = "lc-heading";

  const body = (() => {
    if (c.view === "intake") return <GuidedIntake c={c} lang={lang} userName={userName} />;
    if (c.view === "faq") return <FaqView c={c} lang={lang} />;
    if (c.view === "chat" && c.activeId) return <ChatView c={c} lang={lang} />;
    if (c.view === "settings") return <SettingsView c={c} />;
    if (c.view === "list") return <ConversationList c={c} lang={lang} />;
    return <WelcomeView c={c} />;
  })();

  const widget = (
    <div className="lc-root" dir={lang === "ar" ? "rtl" : "ltr"} lang={lang === "ar" ? "ar" : "en"} data-testid="copilot-widget">
      {hasOpened && (
        <section
          ref={panel}
          id="lc-panel"
          className={`lc-panel ${open ? "open" : ""}`}
          role="dialog"
          aria-labelledby={headingId}
          aria-modal="false"
          hidden={!open}
          onKeyDown={onPanelKeyDown}
        >
          <header className="lc-header">
            {showBack && (
              <Button variant="neutral" appearance="ghost" className="lc-header-btn lc-back" aria-label={c.view === "chat" ? t.back : g.backToStart} title={c.view === "chat" ? t.back : g.backToStart} onClick={c.goBack} icon={<ChevronLeft size={20} />} />
            )}
            <div className="lc-heading">
              <h2 id={headingId} ref={title} tabIndex={-1}>{t.title}</h2>
              <span>{t.subtitle}</span>
            </div>
            {canSettings && (
              <Button variant="neutral" appearance="ghost" className="lc-header-btn" aria-label={t.settings} title={t.settings} aria-pressed={c.view === "settings"} onClick={c.view === "settings" ? c.showHome : c.showSettings} icon={<Bell size={18} />} />
            )}
              <Button variant="neutral" appearance="ghost" className="lc-header-btn" aria-label={c.g.myConversations} title={c.g.myConversations} aria-pressed={c.view === "list"} onClick={c.view === "list" ? c.showHome : c.showList} icon={<MessagesSquare size={18} />} />
            
            <Button variant="neutral" appearance="ghost" className="lc-header-btn" aria-label={t.close} title={t.minimize} onClick={closePanel} icon={<Minus size={20} />} />
          </header>
          {c.notice && (
            <div className="lc-notice" role="status">
              <NoticeBox variant={c.notice.type === "warning" ? "warning" : "info"} size="sm">
                {c.notice.text}
                {c.notice.signIn && c.mode === "guest" ? <> <a className="lc-signin" href="/signin">{t.signIn}</a></> : null}
              </NoticeBox>
              <Button variant="neutral" appearance="ghost" size="xs" onClick={c.dismissNotice} aria-label={t.dismiss} title={t.dismiss} icon={<X size={14} aria-hidden="true" />} />
            </div>
          )}
          <div className="lc-body">{body}</div>
        </section>
      )}

      <button
        ref={launcher}
        type="button"
        className={`lc-launcher ${open ? "is-open" : ""}`}
        aria-label={unreadLabel}
        aria-expanded={open}
        aria-controls="lc-panel"
        aria-haspopup="dialog"
        onClick={toggle}
      >
        <MessageCircle size={26} aria-hidden="true" />
        {c.unread > 0 && (
          <span className="lc-badge" data-testid="copilot-badge" aria-hidden="true">
            {c.unread > 9 ? "9+" : c.unread}
          </span>
        )}
      </button>
    </div>
  );

  return createPortal(widget, document.body);
}
